import React, { useCallback, useEffect, useState } from "react";
import { useNotify } from "../../common/components/AlertNotify";
import {
  checkEdsInSignme,
  deleteSignMeDocument,
  signmeActivate,
  signmeDeleteRegistrationPerson,
  signmeRegistrationPerson,
  uploadRegistrationDocs,
  uploadSignMeDocument,
} from "../signme";
import { exceptionsTitle } from "../../../dictionaries";
import { CircularProgress, Grid, IconButton, Link, Stack, Typography } from '@mui/material';
import { ButtonBase, ButtonUpload } from "../../common/components/Button";
import {
  IndentSpace,
  Link1,
  Title4,
  Title5,
} from "../../common/components/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  getParticipantsSignatures,
  updateSignmeParticipantsData,
} from "../../../pages/deal/signatures";

import { BlankRow, BoolChip, DocRow, Row } from './Common';
import Box from "@mui/material/Box";

/**
 * Контейнер данными по регистрации и УКЭП в Сайнми
 * 1. Если нет сведений о регистрации в Сайнми у пользователя, то сперва даем кнопку Проверить
 * 2. Получив данные проверяем если пользователь зарегистрировался в Сайнми, то выводим инфу по регистрации и УКЭП
 *    Если пользователь не зарегался то выводим Кнопку зарегистрироваться.
 *    На этапе регистрации все поля должны быть заполнены
 * 3. Регистрация,
 * 3.1 Сперва регаем пользователя
 * 3.2 Загружаем документы по пользователю - паспорт, снилс ...
 * 3.3 Отправляем запрос на активацию (Выпуск УКЭП)
 * @param values
 * @returns {Element}
 * @constructor
 */
export const SubjectSignmeSignature = ({ subjectId }) => {
  const [state, setState] = useState(null);

  const [registrationTitle, setRegistrationTitle] = useState("Запросить бланк");
  const [deleteRegistrationTitle, setDeleteRegistrationTitle] = useState(
    "Удалить заявку на выпуск ЭЦП"
  );
  const [docUploadTitle, setDocUploadTitle] = useState("Отправить в УЦ");
  const [activatePersonTitle, setActivatePersonTitle] = useState(
    "Подтвердить пользователя"
  );
  const { openNotify } = useNotify();

  const loadData = async () => {
    const result = await getParticipantsSignatures({ subjectId });

    if (result) {
      setState(result.signme);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const registrationPerson = useCallback(async () => {
    setRegistrationTitle("Регистрация ...");
    const result = await signmeRegistrationPerson(subjectId);

    if (result.ok) {
      openNotify({
        msg: "Бланк заявления успешно сформирован",
        duration: 3000,
        type: "success",
      });

      setState({...state, ...result.data.signme});
    } else {
      openNotify({
        msg:
          exceptionsTitle[result?.message] ||
          result?.message ||
          "Неизвестная ошибка",
        duration: 7000,
        type: "error",
      });
    }

    setRegistrationTitle("Запросить бланк");
  });

  const deleteRegistrationPerson = useCallback(async () => {
    setDeleteRegistrationTitle("Удаление регистрации ...");
    const result = await signmeDeleteRegistrationPerson(subjectId);

    if (result.ok) {
      openNotify({
        msg: result.data?.body?.message || "Успешно удален",
        duration: 2000,
        type: "success",
      });
      setState(result.data);
    } else {
      openNotify({
        msg:
          exceptionsTitle[result?.message] ||
          result?.message ||
          "Неизвестная ошибка",
        duration: 7000,
        type: "error",
      });
    }

    setDeleteRegistrationTitle("Удалить заявку на выпуск ЭЦП");
  });

  const uploadRegistrationDocuments = useCallback(async () => {
    setDocUploadTitle("Идёт отправка документов ...");

    const result = await uploadRegistrationDocs(subjectId);

    if (result.ok) {
      setState({ ...state, documentsAlreadySentToCA: true });
      updateSignmeParticipantsData({
        subjectId,
        data: { documentsAlreadySentToCA: true },
      });
      openNotify({
        msg: "Документы успешно отправлены",
        duration: 2000,
        type: "success",
      });
    } else {
      openNotify({
        msg: exceptionsTitle[result?.message] || "Ошибка отправки документов",
        duration: 7000,
        type: "error",
      });
    }

    setDocUploadTitle("Отправить в УЦ");
  });

  const activatePerson = useCallback(async () => {
    setActivatePersonTitle("Активация ...");
    const result = await signmeActivate(subjectId);

    if (result.ok) {
      openNotify({
        msg: "Успешно подтвержден",
        duration: 3000,
        type: "success",
      });
      setState(result.data.signme);
    } else {
      openNotify({
        msg:
          exceptionsTitle[result?.message] ||
          result?.message ||
          "Неизвестная ошибка",
        duration: 7000,
        type: "error",
      });
    }

    setActivatePersonTitle("Подтвердить пользователя");
  });

  const RegistrationSteps = useCallback(() => {
    const registrationContainer = (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <ButtonBase variant="contained" size="small">
            <Link
              href="#"
              color="inherit"
              onClick={registrationPerson}
              sx={{ textDecoration: "none", fontSize: 12 }}
            >
              {registrationTitle}
            </Link>
          </ButtonBase>
          <IndentSpace size={10} />
          <ButtonBase variant="contained" color="error" size="small">
            <Link
              href="#"
              color="inherit"
              onClick={deleteRegistrationPerson}
              sx={{ textDecoration: "none", fontSize: 12 }}
            >
              {deleteRegistrationTitle}
            </Link>
          </ButtonBase>
        </Grid>
        <Grid item xs={12}>
          {state?.registrationBlank ? (
            <BlankRow
              href={state?.registrationBlank}
              label="Бланк заявления на регистрацию"
            />
          ) : (
            void 0
          )}
        </Grid>
        {state?.userinfo?.haspdf ? (
          <Grid item xs={12}>
            <BlankRow
              href={state?.userinfo?.haspdf}
              label="Ранее подписанное заявление"
            />
          </Grid>
        ) : (
          void 0
        )}
        <Grid item xs={12}></Grid>
      </Grid>
    );

    return registrationContainer;
  });

  return (
    <Grid container spacing={1} sx={{ width: "552px" }}>
      <Grid item xs={12}>
        <Title4 title="SignMe" />
      </Grid>
      <Grid item xs={12}>
        <Title5 title="Сведения о регистрации" />
      </Grid>
      <Grid item xs={12}>
        <CheckResultInfo data={state} subjectId={subjectId} />
      </Grid>
      <Grid item xs={12} style={{ height: 20 }}></Grid>
      <Grid item xs={12}>
        <Title5 title="Регистрация" />
      </Grid>
      <Grid item xs={12}>
        <RegistrationSteps />
      </Grid>
      <Grid item xs={12}>
        <SubjectSignmeDocUpload
          issueDocs={state}
          subjectId={subjectId}
          onChange={() => loadData()}
        ></SubjectSignmeDocUpload>
      </Grid>
      <Grid item xs={12} style={{ height: 12 }}></Grid>
      <Grid item xs={12}>
        <ButtonBase variant="contained" size="small">
          <Link
            href="#"
            color="inherit"
            onClick={uploadRegistrationDocuments}
            sx={{ textDecoration: "none", fontSize: 12 }}
          >
            {docUploadTitle}
          </Link>
        </ButtonBase>
        <IndentSpace size={10} />
        <ButtonBase
          variant="contained"
          color="warning"
          size="small"
          disabled={!state?.documentsAlreadySentToCA}
        >
          <Link
            href="#"
            color="inherit"
            onClick={activatePerson}
            sx={{ textDecoration: "none", fontSize: 12 }}
          >
            {activatePersonTitle}
          </Link>
        </ButtonBase>
      </Grid>
    </Grid>
  );
};

const CheckResultInfo = ({ subjectId, data }) => {
  const [values, setValues] = useState(data);
  const [prechecking, setPrechecking] = useState(false);
  const { openNotify } = useNotify();

  useEffect(() => {
    setValues(data);
  }, [data]);

  const precheck = async () => {
    setPrechecking(true);
    const result = await checkEdsInSignme(subjectId);

    if (!result.ok) {
      openNotify({
        msg:
          exceptionsTitle[result?.message] ||
          result?.message ||
          "Неизвестная ошибка",
        duration: 7000,
        type: "error",
      });
    } else {
      setValues(result.data.signme);
      updateSignmeParticipantsData({ data: result.data.signme, subjectId });
    }
    setPrechecking(false);
  };

  // Формируем массив ключей ЭП
  const keys = values?.precheck?.keys?.map(
    (item) => `- ${item.id} Квалифицированная - ${item.qualified ? "да" : "нет"}, истекает - ${item.expired_at}`
  ) || [];

  // Формируем строки для отображения
  const rows = values?.precheck?.id
    ? [
      {
        label: "Номер заявки",
        value: values.precheck.id,
      },
      {
        label: "Пользователь создал пароль и активировал кабинет",
        valueEl: <BoolChip value={values.precheck.created} />,
      },
      keys.length > 0 && {
        label: "Имеющиеся ЭЦП",
        valueEl: (
          <Stack spacing={0.5}>
            {keys.map((key, idx) => (
              <Typography key={idx} variant="body2">
                {key}
              </Typography>
            ))}
          </Stack>
        ),
      },
    ].filter(Boolean)
    : [];

  return (
    <Box sx={{ maxWidth: 500 }}>
      <Box item xs={12}>
        <ButtonBase variant="contained" color="success" size="small">
          <Link
            href="#"
            color="inherit"
            onClick={precheck}
            sx={{
              textDecoration: "none",
              fontSize: 12,
            }}
          >
            {prechecking
              ? "Идёт запрос данных ..."
              : "Проверить наличия ЭП"}
          </Link>
        </ButtonBase>
      </Box>

      {rows.length > 0 ? (
        <Stack
          divider={
            <Box sx={{ borderTop: "1px solid", borderColor: "grey.100" }} />
          }
        >
          {rows.map((row, i) => (
            <Row key={i} {...row} />
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: "text.disabled", py: 1 }}>
          Нажмите «Проверить» для получения данных
        </Typography>
      )}
    </Box>
  );
};

const SubjectSignmeDocUpload = ({ issueDocs, subjectId, onChange }) => {
  const [state, setState] = useState(issueDocs || {});
  const [loadingIcon, setLoadingIcon] = useState({
    passportPhotoPage: false,
    subjectPhotoWStatement: false,
    passportRegistration: false,
    inn: false,
    issueStatement: false,
    snils: false,
  });
  const { openNotify } = useNotify();

  useEffect(() => {
    setState(issueDocs);
  }, [issueDocs]);

  // Конфигурация полей документов
  const documentFields = [
    {
      label: "Паспорт страница с фото",
      field: "passportPhotoPage",
      required: true,
    },
    {
      label: "СНИЛС",
      field: "snils",
      required: true,
    },
    {
      label: "Заявление на выпуск ЭЦП",
      field: "issueStatement",
      required: true,
      customStyle: { padding: 0 },
    },
    {
      label: "ИНН",
      field: "inn",
      required: true,
    },
    {
      label: "Фото клиента с заявлением",
      field: "subjectPhotoWStatement",
      required: false,
      labelColor: "#8d8d8d",
    },
  ];

  const handleUploadResult = useCallback(async (docType, event) => {
    setLoadingIcon((prev) => ({ ...prev, [docType]: true }));

    const result = await uploadSignMeDocument(
      docType,
      event.formData,
      subjectId
    );

    if (result.ok) {
      setState((prev) => ({ ...prev, ...result.data }));

      updateSignmeParticipantsData({ subjectId, data: { ...result.data } });
      onChange();
    } else {
      openNotify({
        errors: result.errors,
        msg: exceptionsTitle[result.message] || "Не удалось загрузить файл",
        duration: 5000,
        type: "error",
      });
    }

    setLoadingIcon((prev) => ({ ...prev, [docType]: false }));
  });

  const onDelete = useCallback(async (docType) => {
    const result = await deleteSignMeDocument(docType, subjectId);

    if (result) {
      const dtlink = `${docType}Link`;

      setState((prev) => {
        const newState = { ...prev };
        // Удаляем файл и ссылку
        delete newState[docType];
        delete newState[`${docType}Link`];
        return newState;
      });

      updateSignmeParticipantsData({
        subjectId,
        data: { [dtlink]: "", [docType]: "" },
      });
      onChange();
    }
  });

  return (
    <>
      <Box sx={{ borderTop: "1px solid", borderColor: "grey.100" }} />
      <Box sx={{ pt: 2 }}>
        <Title5 title="Документы" />
      </Box>
      {documentFields.map((f, idx) => (
        <DocRow
          key={f.field}
          {...f}
          fileName={state?.[f.field]}
          fileLink={state?.[`${f.field}Link`]}
          isLoading={loadingIcon[f.field]}
          onUpload={handleUploadResult}
          onDelete={onDelete}
          isLast={idx === documentFields.length - 1}
        />
      ))}
    </>
  );
};